<script setup lang="ts">
import { isDefined, userSlimId, type UserSlim } from '@webapp2/shared/model'
import { computed } from 'vue'
import { injectOrThrow } from '../../inject'
import MultiSelect from '../primevue/MultiSelect.vue'
import type { Option } from '../primevue/select'
import User from '../User.vue'
import UserAvatar from '../UserAvatar.vue'
import { userSelectProviderKey, type UserSelectProvider } from './user-select'

const provider = injectOrThrow<UserSelectProvider>(userSelectProviderKey)

const model = defineModel<string[]>({ default: [] })

const selectedUserIds = computed<string[]>({
  get() {
    return model.value.map(x => userSlimId(x))
  },
  set(value) {
    model.value = value.map(x => provider.getUserSlim(x)?.userId).filter(isDefined)
  }
})

function toOption(user: UserSlim): Option {
  return { label: user.displayName, value: user.id, user } as Option
}

async function loadOptions(filterText: string): Promise<Option[]> {
  const users = await provider.loadUserSlims(filterText)
  return users.map(toOption)
}

async function ensureSelected(ids: string[]): Promise<Option[]> {
  await provider.ensureUserSlims(ids)
  return ids
    .map(id => provider.getUserSlim(id))
    .filter(isDefined)
    .map(toOption)
}
</script>

<template>
  <MultiSelect
    v-model="selectedUserIds"
    :load-options="loadOptions"
    :ensure-selected="ensureSelected"
    :pt="{ label: '-space-x-2 h-10' }"
  >
    <template #chip="{ value, removeCallback }">
      <div @click.stop>
        <User
          v-if="selectedUserIds.length === 1"
          :user="provider.getUserSlim(value)"
          :on-clear="removeCallback"
          class="max-w-48"
        />
        <UserAvatar v-else :user="provider.getUserSlim(value)" :on-clear="removeCallback" />
      </div>
    </template>
    <template #option="slotProps">
      <User class="max-w-xl" :user="slotProps.option.user" />
    </template>
  </MultiSelect>
</template>
